import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import InputMask from "react-input-mask";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation } from "@apollo/client";

// region Libraries
import Yup from "@libraries/yup";
import { TextField } from "@libraries/mui";
import { Button, useToast } from "@npm-telluria-tecnologia/telluria-ui";
// endregion Libraries
// region Store
import { ECommandResultStatus, UserMutationsCreateArgs } from "@store/graphql/helpers/types.graphql";
import { TUserCreateMutations, userCreate } from "@store/graphql/groups/user/mutation.graphql";
// endregion Store
// region Language
import useTranslation from "@languages/useTranslation";
import { SignUpMessages } from "@languages/interfaces/signUpMessages";
import { YupMessages } from "@languages/interfaces";
// endregion Language
// region Atoms
import ExternalSvg from "@atoms/ExternalSvg";
// endregion Atoms
// region Molecules
import PageHeader from "@molecules/PageHeader";
// endregion Molecules
// region Styles
import utils from "@helpers/utils";
import * as Styled from "./styles";
// endregion Styles

interface ISignUpFormData {
  email: string;
  fullName: string;
  document: string;
  role: string;
  phoneNumber: string;
  clientName: string;
}

const SignUpForm: React.FC = () => {

  const { t } = useTranslation();
  const { addToast } = useToast();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);

  const [MutationCreateUser] = useMutation<TUserCreateMutations,
    UserMutationsCreateArgs>(
      userCreate([
        "id",
        "email",
        "fullName",
        "document",
        "role",
        "phoneNumber",
        "clientName",
        "createdAt"
      ])
    );

  // Define the validation types
  const signUpSchema = Yup.object()
    .shape({
      email: Yup.string()
        .trim()
        .required(t(SignUpMessages.signUpRequiredEmail))
        .email(t(SignUpMessages.signUpInvalidEmail)),
      fullName: Yup.string()
        .trim()
        .required(t(SignUpMessages.signUpFullNameRequired)),
      document: Yup.string()
        .trim().test(
          "is-valid-cpf",
          t(YupMessages.invalidCpf),
          (value) => {
            if (!value) return true;

            return utils.isValidCpf(value);
          }
        ),
      role: Yup.string()
        .trim(),
      phoneNumber: Yup.string()
        .trim(),
      clientName: Yup.string()
        .trim()
    });

  // Form control
  const {
    control,
    handleSubmit,
    formState: { errors: formErrors }
  } = useForm<ISignUpFormData>({
    resolver: yupResolver(signUpSchema),
    reValidateMode: "onChange",
    mode: "onBlur"
  });

  /**
   * Create new user
   * @param data User data from form
   */
  const createUser = useCallback(
    async (data: ISignUpFormData) => {

      const cleanedCPF = data.document?.replace(/\D/g, "");

      const {
        email,
        fullName,
        role,
        phoneNumber,
        clientName
      } = data;

      const mutation = await MutationCreateUser({
        variables: {
          user: {
            email,
            fullName,
            document: cleanedCPF,
            role,
            phoneNumber,
            clientName
          }
        }
      });

      return mutation.data?.user.create;
    },
    [MutationCreateUser]
  );

  /**
   * Handle submit form
   * @param data User data from form
   */
  const signUp = useCallback(
    async (data: ISignUpFormData) => {

      setIsLoading(true);

      try {

        const response = await createUser(data);

        if (response?.status !== ECommandResultStatus.SUCCESS) return;

        addToast({
          type: "success",
          title: response.message
        });

        navigate("/login");

      } catch (error) {
        addToast({
          type: "error",
          title: "Internal client error",
          description: error.message
        });
      } finally {
        setIsLoading(false);
      }
    },
    [addToast, createUser, navigate]
  );

  // Handle form controllers
  const handleSubmitForm: SubmitHandler<ISignUpFormData> = (formData: ISignUpFormData) => signUp(formData);

  return (
    <Styled.SignUpFormContainer>
      <Styled.SignUpFormLogo>
        <ExternalSvg svgName="LogotipoMinifiedColor" />
      </Styled.SignUpFormLogo>
      <PageHeader url="/login" title={t(SignUpMessages.signUpTitlePage)} />
      <form onSubmit={handleSubmit(handleSubmitForm)}>
        <Controller // Email
          name="email"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              autoComplete="username"
              label={t(SignUpMessages.signUpEmailLabel)}
              type="email"
              variant="outlined"
              fullWidth
              size="small"
              error={!!formErrors.email}
              helperText={formErrors.email && formErrors.email.message}
              className="default-field"
            />
          )}
        />
        <Controller // Full Name
          name="fullName"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              autoComplete="name"
              label={t(SignUpMessages.signUpFullNameLabel)}
              type="text"
              variant="outlined"
              fullWidth
              size="small"
              error={!!formErrors.fullName}
              helperText={formErrors.fullName && formErrors.fullName.message}
              className="default-field"
            />
          )}
        />
        <Controller // Document
          name="document"
          control={control}
          render={({ field }) => (
            <InputMask
              mask="999.999.999-99"
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
            >
              {(inputProps) => (
                <TextField
                  {...field}
                  {...inputProps}
                  label={t(SignUpMessages.signUpDocumentLabel)}
                  type="text"
                  variant="outlined"
                  fullWidth
                  size="small"
                  error={!!formErrors.document}
                  helperText={formErrors.document && formErrors.document.message}
                  className="default-field"
                />
              )}
            </InputMask>
          )}
        />
        <Controller // Role
          name="role"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              autoComplete="role"
              label={t(SignUpMessages.signUpRoleLabel)}
              type="text"
              variant="outlined"
              fullWidth
              size="small"
              error={!!formErrors.role}
              helperText={formErrors.role && formErrors.role.message}
              className="default-field"
            />
          )}
        />
        <Controller // Phone Number
          name="phoneNumber"
          control={control}
          render={({ field }) => (
            <InputMask
              mask="(99) 99999-9999"
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
            >
              {
                (inputProps) => (
                  <TextField
                    {...field}
                    {...inputProps}
                    autoComplete="phone"
                    label={t(SignUpMessages.signUpPhoneNumberLabel)}
                    type="text"
                    variant="outlined"
                    fullWidth
                    size="small"
                    error={!!formErrors.phoneNumber}
                    helperText={
                      formErrors.phoneNumber && formErrors.phoneNumber.message
                    }
                    className="default-field"
                  />
                )
              }
            </InputMask>
          )}
        />
        <Controller // Client Name
          name="clientName"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              autoComplete="off"
              label={t(SignUpMessages.signUpClientNameLabel)}
              type="text"
              variant="outlined"
              fullWidth
              size="small"
              error={!!formErrors.clientName}
              helperText={formErrors.clientName && formErrors.clientName.message}
              className="default-field"
            />
          )}
        />
        <Button
          variant="contained"
          size="small"
          type="submit"
          text={t(SignUpMessages.signUpButton)}
          loading={isLoading}
        />
      </form>
    </Styled.SignUpFormContainer>
  );
};

export default SignUpForm;
